// PR Inquiry and PO Inquiry API (backend mounted at /api/inquiry). JSON calls answer { success, data }; these
// helpers return `data`. Lists are made arrays, so the screens can rely on their shapes.
import apiService from './api.service';
import type {
  InquiryFilters,
  PoInquiryResult,
  PoLookups,
  PrInquiryResult,
  PrLookups,
  SapPoView,
} from '../types/inquiry';

const BASE = '/inquiry';
/** The first search of a company may index its SAP POs (up to a minute for the largest). */
const TIMEOUT = 180000;

/** The shared axios client (auth header, 401 handling). */
const http = () => (apiService as any).axiosInstance;

/** Query parameters without the empty ones. */
const params = (p: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(p)) if (v !== undefined && v !== null && String(v).trim() !== '') out[k] = v;
  return out;
};

const arr = <T>(v: T[] | null | undefined): T[] => (Array.isArray(v) ? v : []);
const noNames = () => ({ projects: {}, divisions: {}, departments: {} });

async function get<T>(url: string, query: Record<string, unknown>): Promise<T> {
  const res = await http().get(url, { params: params(query), timeout: TIMEOUT });
  return res.data?.data as T;
}

async function blob(url: string, query: Record<string, unknown>): Promise<Blob> {
  const res = await http().get(url, { params: params(query), responseType: 'blob', timeout: 300000 });
  return res.data as Blob;
}

const inquiryApi = {
  prLookups: async (companyId: number): Promise<PrLookups> => {
    const d = await get<PrLookups>(`${BASE}/pr/lookups`, { companyId });
    return { ...d, statuses: arr(d?.statuses), types: arr(d?.types), requesters: arr(d?.requesters),
      codes: { projects: arr(d?.codes?.projects), divisions: arr(d?.codes?.divisions), departments: arr(d?.codes?.departments) } };
  },
  prSearch: async (companyId: number, filters: InquiryFilters): Promise<PrInquiryResult> => {
    const d = await get<PrInquiryResult>(`${BASE}/pr/search`, { companyId, ...filters });
    return { ...d, rows: arr(d?.rows).map((r) => ({ ...r, pos: arr(r.pos), sapPrNumbers: arr(r.sapPrNumbers),
      projects: arr(r.projects), divisions: arr(r.divisions), departments: arr(r.departments) })), names: d?.names || noNames() };
  },
  prExport: (companyId: number, filters: InquiryFilters) => blob(`${BASE}/pr/export`, { companyId, ...filters }),

  poLookups: async (companyId: number): Promise<PoLookups> => {
    const d = await get<PoLookups>(`${BASE}/po/lookups`, { companyId });
    return { ...d, statuses: arr(d?.statuses), requesters: arr(d?.requesters), sapSync: d?.sapSync || { count: 0, syncedAt: null },
      codes: { projects: arr(d?.codes?.projects), divisions: arr(d?.codes?.divisions), departments: arr(d?.codes?.departments) } };
  },
  poSearch: async (companyId: number, filters: InquiryFilters): Promise<PoInquiryResult> => {
    const d = await get<PoInquiryResult>(`${BASE}/po/search`, { companyId, ...filters });
    return { ...d, rows: arr(d?.rows).map((r) => ({ ...r, prs: arr(r.prs), projects: arr(r.projects), divisions: arr(r.divisions),
      departments: arr(r.departments) })), totals: arr(d?.totals), names: d?.names || noNames(), sapSync: d?.sapSync || { count: 0, syncedAt: null } };
  },
  poExport: (companyId: number, filters: InquiryFilters) => blob(`${BASE}/po/export`, { companyId, ...filters }),

  sapPo: async (companyId: number, docEntry: number): Promise<SapPoView> => {
    const d = await get<SapPoView>(`${BASE}/po/sap/${docEntry}`, { companyId });
    return { ...d, prs: arr(d?.prs), lines: arr(d?.lines), names: d?.names || noNames(),
      receipts: { documents: arr(d?.receipts?.documents).map((g) => ({ ...g, lines: arr(g.lines) })), asOf: d?.receipts?.asOf ?? null } };
  },
};

export default inquiryApi;
